<template>
  <div>
    <!-- Exit Intent Modal -->
    <b-modal
      id="exit-intent-modal"
      v-model="showModal"
      centered
      hide-header
      hide-footer
      no-close-on-backdrop
      no-close-on-esc
      modal-class="exit-intent-modal"
      dialog-class="exit-intent-dialog"
      @hidden="onModalHidden"
    >
      <div class="exit-modal-inner">
        <!-- Dismiss button -->
        <button class="dismiss-btn" @click="dismissModal" aria-label="Close">
          <span>&times;</span>
        </button>

        <!-- Progress dots -->
        <div class="progress-dots" v-if="currentStep < 6">
          <span
            v-for="n in 6"
            :key="n"
            class="dot"
            :class="{ active: n === currentStep, done: n < currentStep }"
          ></span>
        </div>

        <!-- ── STEP 1 ── -->
        <transition name="step-fade" mode="out-in">
          <div v-if="currentStep === 1" key="step1" class="step-content">
            <h2 class="step-title">
              El actor que avanza queriendo actuar en todo, termina sin trabajar en nada.
            </h2>
            <p class="step-body">
              Responde 4 preguntas en menos de un minuto y te decimos los 4 pilares del mapa del actor para acercarte a castings y proyectos reales.
            </p>
            <button class="cta-btn" @click="nextStep">Quiero conocer el mapa</button>
          </div>

          <!-- ── STEP 2 ── -->
          <div v-else-if="currentStep === 2" key="step2" class="step-content">
            <div class="step-number">01 / 04</div>
            <h2 class="step-question">¿Dónde estás hoy en tu carrera?</h2>
            <div class="checkbox-group">
              <label v-for="(opt, i) in step2Options" :key="i" class="checkbox-item" :class="{ selected: step2Selected === opt }">
                <input type="radio" :value="opt" v-model="step2Selected" hidden />
                <span class="checkbox-box">
                  <svg v-if="step2Selected === opt" viewBox="0 0 12 10" fill="none"><polyline points="1,5 4.5,8.5 11,1" stroke="#000" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
                </span>
                <span class="checkbox-label">{{ opt }}</span>
              </label>
            </div>
            <button class="cta-btn" :disabled="step2Selected === null" @click="nextStep">Siguiente</button>
          </div>

          <!-- ── STEP 3 ── -->
          <div v-else-if="currentStep === 3" key="step3" class="step-content">
            <div class="step-number">02 / 04</div>
            <h2 class="step-question">¿Qué es lo que más te frena ahora?</h2>
            <div class="checkbox-group">
              <label v-for="(opt, i) in step3Options" :key="i" class="checkbox-item" :class="{ selected: step3Selected === opt }">
                <input type="radio" :value="opt" v-model="step3Selected" hidden />
                <span class="checkbox-box">
                  <svg v-if="step3Selected === opt" viewBox="0 0 12 10" fill="none"><polyline points="1,5 4.5,8.5 11,1" stroke="#000" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
                </span>
                <span class="checkbox-label">{{ opt }}</span>
              </label>
            </div>
            <button class="cta-btn" :disabled="step3Selected === null" @click="nextStep">Siguiente</button>
          </div>

          <!-- ── STEP 4 ── -->
          <div v-else-if="currentStep === 4" key="step4" class="step-content">
            <div class="step-number">03 / 04</div>
            <h2 class="step-question">¿Cuál es tu correo electrónico?</h2>
            <div class="field-wrap">
              <input
                v-model="email"
                type="email"
                class="text-field"
                placeholder="tucorreo@ejemplo.com"
                @keyup.enter="emailValid && nextStep()"
              />
            </div>
            <button class="cta-btn" :disabled="!emailValid" @click="nextStep">Siguiente</button>
          </div>

          <!-- ── STEP 5 ── -->
          <div v-else-if="currentStep === 5" key="step5" class="step-content">
            <div class="step-number">04 / 04</div>
            <h2 class="step-question">¿Cuál es tu WhatsApp de contacto?</h2>
            <div class="field-wrap">
              <input
                v-model="phone"
                type="tel"
                class="text-field"
                placeholder="+52 55 1234 5678"
                @keyup.enter="phoneValid && nextStep()"
              />
            </div>
            <button class="cta-btn" :disabled="!phoneValid" @click="nextStep">Gracias! Ahora sí… Conoce los 4 pilares</button>
          </div>

          <!-- ── STEP 6 ── -->
          <div v-else-if="currentStep === 6" key="step6" class="step-content step6">
            <h2 class="step-title step6-title">Gracias. Este es el mapa con sus 4 pilares:</h2>
            <div class="pillars">
              <div class="pillar-item" v-for="(p, i) in pillars" :key="i" :style="{ animationDelay: (i * 0.1 + 0.1) + 's' }">
                <span class="pillar-num">{{ i + 1 }}</span>
                <div>
                  <strong class="pillar-name">{{ p.name }}</strong>
                  <p class="pillar-desc">{{ p.desc }}</p>
                </div>
              </div>
            </div>
            <button class="cta-btn cta-final" @click="onAccept">Sí, quiero lograr esto para mi carrera</button>
          </div>
        </transition>
      </div>
    </b-modal>

    <slot />
  </div>
</template>

<script>
export default {
  name: 'ExitIntentPopup',

  props: {
    delay: { type: Number, default: 20000 },
  },

  data() {
    return {
      showModal: false,
      currentStep: 1,

      step2Selected: null,
      step2Options: [
        'Estoy estancadx, no me llegan oportunidades',
        'Hago castings, pero no me quedo en nada',
        'Estoy empezando y no sé por dónde',
        'Me va bien, pero quiero subir de nivel',
      ],

      step3Selected: null,
      step3Options: [
        'No sé a quién enviar mi demo para entrar a castings',
        'No sé cómo avanzar si no vivo en Ciudad de México',
        'No sé qué hacer para actuar en series y películas',
        'No me eligen en los castings',
      ],

      email: '',
      phone: '',

      pillars: [
        { name: 'Rumbo claro', desc: 'Qué quieres y hacia dónde vas de acuerdo a tu objetivo real.' },
        { name: 'Entrenamiento que sí suma', desc: 'Lo que te hace mejorar como actor y audicionar más.' },
        { name: 'Presencia y marca personal', desc: 'Cómo te ven y cómo te recuerdan.' },
        { name: 'Conexión con la industria', desc: 'Cómo acercarte a quienes te contratan.' },
      ],
    };
  },

  computed: {
    emailValid() {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.email);
    },
    phoneValid() {
      return this.phone.trim().length >= 7;
    },
  },

  mounted() {
    this.$_timer = setTimeout(() => {
      this.showModal = true;
      this.$emit('triggered');
    }, this.delay);
  },

  beforeDestroy() {
    clearTimeout(this.$_timer);
  },

  methods: {
    nextStep() {
      if (this.currentStep < 6) this.currentStep++;
    },

    dismissModal() {
      this.showModal = false;
      this.$emit('dismissed');
    },

    onAccept() {
      this.showModal = false;
      this.$emit('accepted', { email: this.email, phone: this.phone, step2: this.step2Selected, step3: this.step3Selected });
    },

    onModalHidden() {
      // Reset for next time
      this.currentStep = 1;
      this.step2Selected = null;
      this.step3Selected = null;
      this.email = '';
      this.phone = '';
    },
  },
};
</script>

<style scoped>
/* ── Bootstrap modal overrides ───────────────────────────────────── */
::v-deep .exit-intent-modal .modal-content {
  background: transparent;
  border: none;
  border-radius: 28px;
  overflow: visible;
  box-shadow: none;
}

::v-deep .exit-intent-modal .modal-body {
  padding: 0;
}

::v-deep .exit-intent-modal .modal-dialog {
  max-width: 480px;
  z-index: 999999;
}

/* ── Inner container ─────────────────────────────────────────────── */
.exit-modal-inner {
  position: relative;
  background: #0a0a0a;
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 28px;
  padding: 2.5rem 2rem 2rem;
  text-align: center;
  overflow: hidden;
  animation: modalIn 0.42s cubic-bezier(0.34, 1.56, 0.64, 1) both;
}

.exit-modal-inner::before {
  content: '';
  position: absolute;
  top: -60px;
  left: 50%;
  transform: translateX(-50%);
  width: 280px;
  height: 280px;
  pointer-events: none;
}

@keyframes modalIn {
  from { opacity: 0; transform: scale(0.9) translateY(20px); }
  to   { opacity: 1; transform: scale(1) translateY(0); }
}

/* ── Mobile ──────────────────────────────────────────────────────── */
@media (max-width: 575px) {
  ::v-deep .exit-intent-modal .modal-dialog {
    margin: 0;
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    max-width: 100%;
  }

  .exit-modal-inner {
    border-radius: 28px 28px 0 0;
    animation: sheetUp 0.4s cubic-bezier(0.34, 1.3, 0.64, 1) both !important;
  }
}

@keyframes sheetUp {
  from { transform: translateY(100%); opacity: 0.7; }
  to   { transform: translateY(0);    opacity: 1; }
}

/* ── Dismiss ─────────────────────────────────────────────────────── */
.dismiss-btn {
  position: absolute;
  top: 1rem;
  right: 1rem;
  border: none;
  background: transparent;
  color: rgba(255,255,255,1);
  font-size: 1.4rem;
  line-height: 1;
  cursor: pointer;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s, color 0.2s;
  z-index: 10;
}

.dismiss-btn:hover {
  background: transparent !important;
  color: #fff;
}

/* ── Progress dots ───────────────────────────────────────────────── */
.progress-dots {
  display: flex;
  justify-content: center;
  gap: 6px;
  margin-bottom: 1.5rem;
}

.dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: rgba(255,255,255,0.15);
  transition: all 0.3s;
}

.dot.active {
  background: #2CFAC3;
  transform: scale(1.25);
  box-shadow: 0 0 8px rgba(44,250,195,0.6);
}

.dot.done {
  background: rgba(44,250,195,0.4);
}

/* ── Step transitions ────────────────────────────────────────────── */
.step-fade-enter-active,
.step-fade-leave-active {
  transition: opacity 0.25s ease, transform 0.25s ease;
}

.step-fade-enter {
  opacity: 0;
  transform: translateX(24px);
}

.step-fade-leave-to {
  opacity: 0;
  transform: translateX(-24px);
}

/* ── Step content base ───────────────────────────────────────────── */
.step-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0;
}

.step-number {
  font-family: 'NewBlackTypeface';
  font-size: 0.65rem;
  letter-spacing: 0.15em;
  color: #2CFAC3;
  margin-bottom: 0.75rem;
  text-transform: uppercase;
}

.step-tag {
  font-family: 'NewBlackTypeface';
  font-size: 0.6rem;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: #2CFAC3;
  background: rgba(44,250,195,0.1);
  border: 1px solid rgba(44,250,195,0.25);
  border-radius: 100px;
  padding: 0.3rem 0.9rem;
  margin-bottom: 1.25rem;
}

/* ── Step 1 ──────────────────────────────────────────────────────── */
.step-title {
  font-family: 'NewBlackTypeface';
  font-weight: bold;
  font-size: 1.45rem;
  font-weight: 700;
  color: #fff;
  line-height: 1.25;
  margin-bottom: 1rem;
  letter-spacing: -0.01em;
}

.step-body {
  font-size: 0.9rem;
  color: rgba(255,255,255,0.55);
  line-height: 1.6;
  max-width: 360px;
  margin: 0 auto 1.75rem;
}

/* ── Step question ───────────────────────────────────────────────── */
.step-question {
  font-family: 'NewBlackTypeface';
  font-size: 1.25rem;
  font-weight: 700;
  color: #fff;
  line-height: 1.3;
  margin-bottom: 1.25rem;
}

/* ── Checkbox group ──────────────────────────────────────────────── */
.checkbox-group {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  margin-bottom: 1.5rem;
  text-align: left;
}

.checkbox-item {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  border-radius: 12px;
  border: 1px solid rgba(255,255,255,0.08);
  background: rgba(255,255,255,0.03);
  cursor: pointer;
  transition: all 0.2s;
}

.checkbox-item:hover {
  border-color: rgba(44,250,195,0.3);
  background: rgba(44,250,195,0.05);
}

.checkbox-item.selected {
  border-color: #2CFAC3;
  background: rgba(44,250,195,0.1);
}

.checkbox-box {
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  border-radius: 6px;
  border: 2px solid rgba(255,255,255,0.2);
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 1px;
  transition: all 0.2s;
}

.checkbox-item.selected .checkbox-box {
  background: #2CFAC3;
  border-color: #2CFAC3;
}

.checkbox-box svg {
  width: 12px;
  height: 10px;
}

.checkbox-label {
  font-size: 0.875rem;
  color: rgba(255,255,255,0.75);
  line-height: 1.4;
}

.checkbox-item.selected .checkbox-label {
  color: #fff;
}

/* ── Text fields ─────────────────────────────────────────────────── */
.field-wrap {
  position: relative;
  width: 100%;
  margin-bottom: 1.5rem;
}

.text-field {
  width: 100%;
  background: rgba(255,255,255,0.05);
  border: 1px solid rgba(255,255,255,0.12);
  border-radius: 12px;
  padding: 0.9rem 1rem 0.9rem 3rem;
  font-size: 0.95rem;
  color: #fff;
  outline: none;
  transition: border-color 0.2s, box-shadow 0.2s;
  box-sizing: border-box;
}

.text-field::placeholder {
  color: rgba(255,255,255,0.25);
}

.text-field:focus {
  border-color: #2CFAC3;
  box-shadow: 0 0 0 3px rgba(44,250,195,0.12);
}

.field-icon {
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  font-size: 1rem;
  opacity: 0.5;
  pointer-events: none;
}

/* ── CTA button ──────────────────────────────────────────────────── */
.cta-btn {
  width: 100%;
  max-width: 380px;
  padding: 0.9rem 1.5rem;
  font-family: 'NewBlackTypeface';
  font-size: 1rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  background: #2CFAC3;
  border: none;
  color: #000;
  border-radius: 10px;
  cursor: pointer;
  transition: transform 0.15s, box-shadow 0.15s, opacity 0.2s;
  position: relative;
  overflow: hidden;
}

.cta-btn::after {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, rgba(255,255,255,0.15) 0%, transparent 60%);
  pointer-events: none;
}

.cta-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 28px rgba(44,250,195,0.35);
}

.cta-btn:active:not(:disabled) {
  transform: translateY(0);
}

.cta-btn:disabled {
  opacity: 0.35;
  cursor: not-allowed;
}

.cta-final {
  background: #5864FF;
  color: #fff;
  font-size: 0.78rem;
}

.cta-final:hover:not(:disabled) {
  box-shadow: 0 8px 28px rgba(88,100,255,0.4);
}

/* ── Step 6 ──────────────────────────────────────────────────────── */
.step6 {
  text-align: left;
}

.step6-title {
  text-align: center;
  font-size: 1.2rem;
  margin-bottom: 1.25rem;
}

.confetti-row {
  text-align: center;
  font-size: 2.5rem;
  margin-bottom: 0.75rem;
  animation: popIn 0.5s cubic-bezier(0.34,1.56,0.64,1) both;
}

@keyframes popIn {
  from { opacity:0; transform: scale(0.4); }
  to   { opacity:1; transform: scale(1);   }
}

.pillars {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  margin-bottom: 1.5rem;
}

.pillar-item {
  display: flex;
  align-items: flex-start;
  gap: 0.85rem;
  background: rgba(255,255,255,0.04);
  border: 1px solid rgba(255,255,255,0.07);
  border-radius: 12px;
  padding: 0.85rem 1rem;
  opacity: 0;
  animation: slideIn 0.4s ease forwards;
}

@keyframes slideIn {
  from { opacity:0; transform: translateY(10px); }
  to   { opacity:1; transform: translateY(0); }
}

.pillar-num {
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  background: #2CFAC3;
  color: #000;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'NewBlackTypeface';
  font-size: 0.75rem;
  font-weight: 700;
  margin-top: 2px;
}

.pillar-name {
  font-size: 0.9rem;
  color: #fff;
  display: block;
  margin-bottom: 0.2rem;
}

.pillar-desc {
  font-size: 0.78rem;
  color: rgba(255,255,255,0.45);
  line-height: 1.5;
  margin: 0;
}
</style>